import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, 
  Users, 
  Plus,
  ChevronDown,
  ChevronUp,
  FileText,
  Building2,
  Award,
  CreditCard,
  Bell,
  Receipt,
  Plug,
  Settings,
  Send,
  ClipboardList,
  BarChart3
} from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../contexts/AuthContext';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const { hasMenuAccess, hasSubmenuAccess, canCreate } = usePermissions();

  const toggleMenu = (menuName: string) => {
    setExpandedMenus(prev => 
      prev.includes(menuName) 
        ? prev.filter(name => name !== menuName)
        : [...prev, menuName]
    );
  };

  // Função para verificar se o menu deve ser exibido
  const shouldShowMenu = (menuPath: string): boolean => {
    return hasMenuAccess(menuPath);
  };

  // Função para verificar se o submenu deve ser exibido
  const shouldShowSubmenu = (menuPath: string, submenuPath: string): boolean => {
    return hasSubmenuAccess(menuPath, submenuPath);
  };

  const navigation = [
    {
      name: 'Início',
      href: '/',
      icon: Home,
      current: location.pathname === '/',
      hasDropdown: false,
      requiresPermission: () => shouldShowMenu('/') // Sempre permitir acesso ao início
    },
    {
      name: 'Alunos',
      href: '/alunos',
      icon: Users,
      current: location.pathname === '/alunos',
      hasDropdown: true
    },
    {
      name: 'Professores',
      href: '/professores',
      icon: Users,
      current: location.pathname === '/professores',
      hasDropdown: true
    },
    {
      name: 'Turmas',
      href: '/turmas',
      icon: Users,
      current: location.pathname === '/turmas',
      hasDropdown: true
    },
    {
      name: 'Níveis',
      href: '/niveis',
      icon: Award,
      current: location.pathname === '/niveis',
      hasDropdown: false
    },
    {
      name: 'Avaliações',
      href: '/avaliacoes',
      icon: ClipboardList,
      current: location.pathname === '/avaliacoes',
      hasDropdown: false
    },
    {
      name: 'Cobranças',
      href: '/cobrancas',
      icon: Receipt,
      current: location.pathname.startsWith('/cobrancas'),
      hasDropdown: true,
      subItems: [
        { name: 'Todas', href: '/cobrancas', icon: FileText },
        { name: 'Avulsas', href: '/cobrancas/avulsas', icon: Receipt },
        { name: 'Assinaturas', href: '/cobrancas/assinaturas', icon: CreditCard },
        { name: 'Notificações', href: '/cobrancas/notificacoes', icon: Bell }
      ]
    },
    {
      name: 'Relatórios',
      href: '/relatorios',
      icon: FileText,
      current: location.pathname === '/relatorios',
      hasDropdown: false
    },
    {
      name: 'Configurações',
      href: '/configuracoes',
      icon: Building2,
      current: location.pathname === '/configuracoes',
      hasDropdown: false
    },
    {
      name: 'Integração',
      href: '/integracao',
      icon: Plug,
      current: location.pathname.startsWith('/integracao'),
      hasDropdown: true,
      subItems: [
        { name: 'Configurações', href: '/integracao/fireagent/configuracoes', icon: Settings },
        { name: 'Integração Assistida', href: '/integracao/embedded-signup', icon: Plug },
        { name: 'Templates', href: '/integracao/templates', icon: FileText },
        { name: 'Disparo de Mensagens', href: '/integracao/disparo', icon: Send },
        { name: 'Consumo', href: '/integracao/consumo', icon: BarChart3 }
      ]
    }
  ].filter(item => {
    // Se o item tem requiresPermission, usar essa função
    if (item.requiresPermission) {
      return item.requiresPermission();
    }
    // Caso contrário, usar shouldShowMenu padrão
    return shouldShowMenu(item.href);
  });

  return (
    <>
      {/* Mobile sidebar overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-gray-900/80 dark:bg-gray-900/90" onClick={onClose} />
          <div className="fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800">
            <SidebarContent 
              navigation={navigation} 
              expandedMenus={expandedMenus} 
              toggleMenu={toggleMenu} 
              onClose={onClose}
              shouldShowSubmenu={shouldShowSubmenu}
            />
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-64 lg:flex-col">
        <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white dark:bg-gray-800 px-6 pb-4 shadow-sm">
          <SidebarContent 
            navigation={navigation} 
            expandedMenus={expandedMenus} 
            toggleMenu={toggleMenu}
            shouldShowSubmenu={shouldShowSubmenu}
          />
        </div>
      </div>
    </>
  );
};

interface SubItem {
  name: string;
  href: string;
  icon: any;
}

interface NavigationItem {
  name: string;
  href: string;
  icon: any;
  current: boolean;
  hasDropdown: boolean;
  primary?: boolean;
  subItems?: SubItem[];
}

interface SidebarContentProps {
  navigation: NavigationItem[];
  expandedMenus: string[];
  toggleMenu: (menuName: string) => void;
  onClose?: () => void;
  shouldShowSubmenu?: (menuPath: string, submenuPath: string) => boolean;
}

const SidebarContent: React.FC<SidebarContentProps> = ({ navigation, expandedMenus, toggleMenu, onClose, shouldShowSubmenu }) => {
  const location = useLocation();
  const { empresa } = useAuth();
  
  return (
    <>
      {/* Logo */}
      <div className="flex h-16 shrink-0 items-center gap-x-2">
        {empresa?.logoUrl ? (
          <img 
            src={empresa.logoUrl} 
            alt={empresa.nomeFantasia || empresa.razaoSocial || 'Logo'}
            className="h-8 w-8 rounded-lg object-cover"
            onError={(e) => {
              // Fallback para ícone se a imagem falhar
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              const parent = target.parentElement;
              if (parent) {
                const fallback = parent.querySelector('.logo-fallback') as HTMLElement;
                if (fallback) fallback.style.display = 'flex';
              }
            }}
          />
        ) : null}
        <div 
          className={`h-8 w-8 rounded-lg bg-primary-600 flex items-center justify-center logo-fallback ${empresa?.logoUrl ? 'hidden' : ''}`}
        >
          <span className="text-white font-bold text-sm">
            {empresa?.nomeFantasia?.charAt(0)?.toUpperCase() || empresa?.razaoSocial?.charAt(0)?.toUpperCase() || 'SP'}
          </span>
        </div>
        {empresa && (
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
              {empresa.nomeFantasia || empresa.razaoSocial}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {empresa.cnpj}
            </span>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex flex-1 flex-col">
        <ul className="flex flex-1 flex-col gap-y-7">
          <li>
            <ul className="-mx-2 space-y-1">
              {navigation.map((item) => (
                <li key={item.name}>
                  {item.primary ? (
                    <button className="group flex w-full items-center gap-x-3 rounded-md bg-primary-600 px-2 py-2 text-sm font-semibold leading-6 text-white hover:bg-primary-700">
                      <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                      {item.name}
                      <ChevronDown className="ml-auto h-4 w-4" />
                    </button>
                  ) : item.subItems ? (
                    <div>
                      <button
                        onClick={() => toggleMenu(item.name)}
                        className={`group flex w-full items-center gap-x-3 rounded-md px-2 py-2 text-sm font-semibold leading-6 ${
                          item.current
                            ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-400'
                        }`}
                      >
                        <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                        {item.name}
                        {expandedMenus.includes(item.name) ? (
                          <ChevronUp className="ml-auto h-4 w-4" />
                        ) : (
                          <ChevronDown className="ml-auto h-4 w-4" />
                        )}
                      </button>
                      {expandedMenus.includes(item.name) && (
                        <ul className="ml-4 mt-1 space-y-1">
                          {item.subItems
                            .filter((subItem: SubItem) => 
                              !shouldShowSubmenu || shouldShowSubmenu(item.href, subItem.href)
                            )
                            .map((subItem: SubItem) => (
                            <li key={subItem.name}>
                              <Link
                                to={subItem.href}
                                onClick={onClose}
                                className={`group flex w-full items-center gap-x-3 rounded-md px-2 py-2 text-sm leading-6 ${
                                  location.pathname === subItem.href
                                    ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-400'
                                }`}
                              >
                                <subItem.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                                {subItem.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      onClick={onClose}
                      className={`group flex w-full items-center gap-x-3 rounded-md px-2 py-2 text-sm font-semibold leading-6 ${
                        item.current
                          ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-400'
                      }`}
                    >
                      <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                      {item.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </li>
        </ul>
      </nav>
    </>
  );
};

export default Sidebar;
